import { isSourcingActive, type SourcingRun } from "./types/sourcing-run";

const STAGE_LABEL: Record<string, string> = {
  queued: "Sourcing in der Warteschlange…",
  finding: "Kandidaten werden gesucht…",
  enriching: "Profile werden angereichert…",
  scoring: "Kandidaten werden bewertet…",
  analyzing: "KI-Analyse läuft…",
};

export interface SourcingBanner {
  tone: "progress" | "stopped" | "failed" | "degraded";
  title: string;
  /** Admins may stop a running search that isn't already stopping (#88). */
  canStop: boolean;
  detail?: string;
}

/** What the Longlist's search banner shows for the job's latest run, or null
 * when a clean finished run needs no banner (its candidates are the result). */
export function sourcingBanner(
  run: SourcingRun,
  { isAdmin }: { isAdmin: boolean },
): SourcingBanner | null {
  if (run.status === "failed") {
    return {
      tone: "failed",
      title: "Sourcing fehlgeschlagen",
      canStop: false,
      detail: run.error ?? "Unbekannter Fehler — bitte erneut versuchen.",
    };
  }
  if (run.status === "partial") {
    return run.error
      ? {
          tone: "degraded",
          title: "Sourcing mit Einschränkungen",
          canStop: false,
          detail: run.error,
        }
      : null;
  }
  if (run.status === "stopped") {
    // A provider failure before the stop stays visible (review on #95).
    return run.error
      ? { tone: "degraded", title: "Suche gestoppt", canStop: false, detail: run.error }
      : { tone: "stopped", title: "Suche gestoppt", canStop: false };
  }
  if (!isSourcingActive(run.status)) return null;
  if (run.stopRequestedAt)
    return { tone: "progress", title: "Suche wird gestoppt…", canStop: false };
  return {
    tone: "progress",
    title: STAGE_LABEL[run.status] ?? "Sourcing läuft…",
    canStop: isAdmin,
  };
}
